'use client';

import { FC, ChangeEvent, useState, useRef } from 'react';
import { FieldError, UseFormRegisterReturn } from 'react-hook-form';
import { KeyTextField } from '@prismicio/client';
import InfoPopup from '../FormComponents/FormBody/InfoPopup/InfoPopup';
import styles from './Input.module.scss';

interface PhoneInputProps {
  label?: string;
  id: string;
  error?: FieldError;
  register: UseFormRegisterReturn;
  placeholder: string;
  currentValue: string;
  infoPopupData?: { title: KeyTextField; description: KeyTextField };
}

const formatNumber = (value: string) => {
  if (value.length <= 3) return value;
  if (value.length <= 6) return `(${value.slice(0, 3)}) ${value.slice(3)}`;
  return `(${value.slice(0, 3)}) ${value.slice(3, 6)}-${value.slice(6, 10)}`;
};

const unformatNumber = (formattedValue: string) =>
  formattedValue.replace(/\D/g, '');

const PhoneNumberInput: FC<PhoneInputProps> = ({
  label,
  id,
  error,
  register,
  placeholder,
  currentValue,
  infoPopupData,
}) => {
  const [value, setValue] = useState(
    formatNumber(unformatNumber(currentValue || ''))
  );
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const rawValue = unformatNumber(input.value);
    const cursorPosition = input.selectionStart || 0;

    const formattedValue = formatNumber(rawValue);
    const addedChars = formattedValue.length - input.value.length;

    e.target.value = formattedValue;
    setValue(formattedValue);

    setTimeout(() => {
      if (inputRef.current) {
        const newCursorPosition = cursorPosition + addedChars;
        inputRef.current.setSelectionRange(
          newCursorPosition,
          newCursorPosition
        );
      }
    });

    if (register.onChange) register.onChange(e);
  };

  return (
    <div className={`${styles.fieldWrapper} ${error ? styles.errorField : ''}`}>
      {label && (
        <label className={styles.label} htmlFor={id}>
          {label}
          {infoPopupData && <InfoPopup data={infoPopupData} variant="phone" />}
        </label>
      )}
      <input
        className={`${styles.input} ${styles.dobInput}`}
        id={id}
        type="tel"
        placeholder={placeholder}
        value={value}
        onChange={handleChange}
        maxLength={14}
        name={register.name}
        ref={(e) => {
          inputRef.current = e;
          register.ref(e);
        }}
      />
      {error && <p className={styles.error}>{error.message}</p>}
    </div>
  );
};

export default PhoneNumberInput;
